/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Fragment } from 'preact';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { useRef, useCallback, useEffect, useState, useImperativeHandle } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { Keys, isKeyDefined } from '../utils/UNSAFE_keys';
import {
  getPrevNextKeyByCount,
  getFirstVisibleKey,
  getViewportConfig,
  findElementByKey,
  keyExtractor
} from '../utils/PRIVATE_collectionUtils';
import { useCurrentKey } from '../hooks/PRIVATE_useCurrentKey';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { useTabbableModeSet } from '../hooks/PRIVATE_useTabbableModeSet';
import { useSwatchSelection } from '../hooks/PRIVATE_useSwatchSelection';
import { ItemContext, ListItemRendererContext, SelectionDetail } from '../UNSAFE_Collection';
import { LoadMoreCollection } from '../PRIVATE_LoadMoreCollection';
import { styles } from './themes/BaseSwatchViewStyles.css';
import { Props } from '../PRIVATE_SwatchFlexView';
import { MemoizeBaseSwatchViewItem } from './BaseSwatchViewItem';
import { BaseSwatchViewLayout } from './BaseSwatchViewLayout';
import { emptyKeys } from './BaseSwatchViewUtils';

const ITEM_SELECTOR = '[data-oj-key]';

/**
 * Props specific for the GradGridBaseImpl Component
 */
type BaseSwatchViewImplProps<K, D> = Props<K, D> & {
  /**
   * Provide the number of columns to render;
   */
  columns: number;
  /**
   * Swatch dimension, including width and height
   */
  swatchSize: { width?: number; height?: number };
  /**
   * A callback function to update swatch dimension, including width and height
   */
  updateSwatchSize: (width: number, height: number) => void;
};

const BaseSwatchViewImpl = forwardRef(
  <K extends string | number, D>(
    {
      children,
      data,
      onLoadMore = () => {},
      hasMore = false,
      getRowKey,
      currentKey,
      onCurrentKeyChange,
      selectedKeys = emptyKeys as Keys<K>,
      onSelectionChange,
      viewportConfig,
      focusBehavior = 'swatch',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      columns,
      swatchSize,
      updateSwatchSize
    }: BaseSwatchViewImplProps<K, D>,
    ref?: ForwardedRef<HTMLDivElement>
  ) => {
    const rootRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref!, () => rootRef.current!, [rootRef]);

    // tracking the anchor key which is used for shift+click selection
    const anchorKey = useRef<K | undefined>();
    // tracking pending current key in pointer down event
    const pendingCurrentKey = useRef<K>();

    const ariaMultiSelectable = 'false';
    const ariaRowcount = hasMore || !data || columns < 1 ? -1 : Math.ceil(data?.length / columns);
    const ariaColcount = columns < 1 ? -1 : columns;

    const [isFocusBehaviorValid, setIsFocusBehaviorValid] = useState(true);
    const validFocusBehavior = isFocusBehaviorValid ? focusBehavior : 'swatch';

    // ensure current item is scrolled into view when current item is updated
    // during reordering, enable that during keyboard reordering, disable that during mouse reordering
    const scrollCurrItemIntoView = useRef(false);

    const isFetching = useRef(false);

    /*****************************************************************************************/
    /************************************* Tabbable Mode *************************************/
    /*****************************************************************************************/

    const [isCurrentTabbableKey, tabbableModeProps] = useTabbableModeSet(
      rootRef,
      (element) => {
        return keyExtractor(element, ITEM_SELECTOR) as K;
      },
      currentKey,
      onCurrentKeyChange
    );

    /*****************************************************************************************/
    /********************************* Current Key and Focus *********************************/
    /*****************************************************************************************/

    const { currentKeyProps } = useCurrentKey(
      (element) => keyExtractor(element, ITEM_SELECTOR),
      true,
      isCurrentTabbableKey(currentKey),
      // init a function every time to make sure we pass in the latest columns,
      // otherwise the new key might be wrong after resize
      getPrevNextKeyByCount(data, getRowKey, currentKey, -columns),
      getPrevNextKeyByCount(data, getRowKey, currentKey, columns),
      getPrevNextKeyByCount(data, getRowKey, currentKey, -1),
      getPrevNextKeyByCount(data, getRowKey, currentKey, 1),
      currentKey,
      onCurrentKeyChange
    );

    const [showFocusRing, focusRingProps] = useCollectionFocusRing(rootRef, [
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight'
    ]);

    const handleFocus = () => {
      // handle initial focus
      if (
        !isKeyDefined(currentKey) &&
        onCurrentKeyChange &&
        isCurrentTabbableKey(undefined) &&
        rootRef.current
      ) {
        const firstKey =
          pendingCurrentKey.current || (getFirstVisibleKey(rootRef.current, ITEM_SELECTOR) as K);
        if (isKeyDefined(firstKey)) {
          onCurrentKeyChange({ value: firstKey });
        }
      }
    };

    // need to track pointer down element to set currentItem when initial focus happens
    const handlePointerDown = (event: PointerEvent) => {
      const key = keyExtractor(event.target as HTMLElement, ITEM_SELECTOR);
      if (isKeyDefined(key)) {
        pendingCurrentKey.current = key as K;
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // for content focusBehavior, when users tab out, we need to focus back to swatch grid, to ensure
      // the tabbing order works properly, otherwise the focus might get stuck at the current item
      if (validFocusBehavior === 'content' && event.key === 'Tab') {
        rootRef.current?.focus({ preventScroll: true });
      }
      // during keyboard reordering, the current item should be scrolled into view
      const keyHandlers = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && keyHandlers.includes(event.key)) {
        scrollCurrItemIntoView.current = true;
      }
    };

    useEffect(() => {
      if (currentKey != null && rootRef.current) {
        const elem = findElementByKey(rootRef.current, currentKey, ITEM_SELECTOR);
        if (elem) {
          // only update aria-activedescendant for screen reader in default focusBehavior
          if (validFocusBehavior === 'swatch') {
            const cell = elem.closest(`[role=gridcell]`);
            const activeDescendant = rootRef.current.getAttribute('aria-activedescendant');
            if (cell && activeDescendant !== cell.id) {
              rootRef.current.setAttribute('aria-activedescendant', cell.id);
              scrollCurrItemIntoView.current = true;
            }
          }
          // make sure current item is visible
          if (scrollCurrItemIntoView.current) {
            elem.scrollIntoView({ block: 'nearest' });
            scrollCurrItemIntoView.current = false;
          }
        }
      }
      // listen to data because the current item would re-render when data is updated, i.e. the id
      // of current item would be updated, so the currentKey itself couldn't guarantee the root has
      // the latest aria-activedescendant
    }, [currentKey, data, validFocusBehavior]);

    /*****************************************************************************************/
    /*************************************** Selection ***************************************/
    /*****************************************************************************************/

    const handleSelectionChange = useCallback(
      (detail: SelectionDetail<K>) => {
        if (onSelectionChange) {
          if (detail.value.all === false && detail.value.keys.size > 0) {
            // during regular selection, the last key selected by a user gesture is the anchor key
            anchorKey.current = Array.from(detail.value.keys.values()).pop();
          }
          onSelectionChange(detail);
        }
      },
      [anchorKey, onSelectionChange]
    );

    const { selectionProps } = useSwatchSelection(
      (element: HTMLElement) => {
        if (element === rootRef.current) {
          return currentKey === undefined ? null : currentKey;
        }
        return keyExtractor(element, ITEM_SELECTOR) as K;
      },
      selectedKeys,
      false,
      'toggle',
      handleSelectionChange
    );

    /*****************************************************************************************/
    /************************************* Load More *****************************************/
    /*****************************************************************************************/

    const handleLoadMore = useCallback(() => {
      // check isFetching to avoid multiple load more at the same time (details in JET-61117)
      if (!isFetching.current) {
        isFetching.current = true;
        onLoadMore();
      }
    }, [isFetching, onLoadMore]);

    useEffect(() => {
      isFetching.current = false;
      // listen to data and hasMore because when one of them gets updated, the fetching is done
    }, [data, hasMore]);

    /*****************************************************************************************/
    /************************************ Child Component ************************************/
    /*****************************************************************************************/

    const getItemContext = useCallback(
      (context: ItemContext<D>) => {
        const key = getRowKey(context.data);

        return {
          index: context.index,
          data: context.data,
          metadata: { key: key },
          selector: undefined
        };
      },
      [getRowKey]
    );

    const childrenComponent = (context: ItemContext<D>) => {
      const swatchItemContext = getItemContext(context);
      const isTabbable = isCurrentTabbableKey(swatchItemContext.metadata.key);
      const isFocused = currentKey === swatchItemContext.metadata.key && !isTabbable;
      const isFocusRingVisible = isFocused && showFocusRing;
      const itemRenderFunc = (context: ListItemRendererContext<K, D>) => (
        <MemoizeBaseSwatchViewItem
          key={swatchItemContext.metadata.key}
          context={context}
          isTabbable={isTabbable}
          isFocused={isFocused}
          isFocusRingVisible={isFocusRingVisible}
          focusBehavior={validFocusBehavior}
          {...(focusBehavior === 'content' && { setIsFocusBehaviorValid })}
          {...(swatchItemContext.index === 0 && !swatchSize.width && { updateSwatchSize })}>
          {children}
        </MemoizeBaseSwatchViewItem>
      );
      return itemRenderFunc(swatchItemContext);
    };

    const { tabbableModeProps: viewTabbleModeProps } = useTabbableMode();

    viewportConfig = getViewportConfig(rootRef, viewportConfig);
    const collectionComponent = (
      <div role="row">
        <BaseSwatchViewLayout>
          <LoadMoreCollection
            data={data}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            loadMoreThreshold={12} // gap size + padding 8 + 4
            viewportConfig={viewportConfig}>
            {childrenComponent}
          </LoadMoreCollection>
        </BaseSwatchViewLayout>
      </div>
    );

    return (
      <div
        {...mergeProps(
          currentKeyProps,
          focusRingProps,
          viewTabbleModeProps,
          selectionProps,
          {
            onFocus: handleFocus,
            onPointerDown: handlePointerDown,
            onKeyDown: handleKeyDown
          },
          // only enable tabbable mode in default focusBehavior
          { ...(validFocusBehavior === 'swatch' && tabbableModeProps) }
        )}
        ref={rootRef}
        role="grid"
        className={styles.baseStyle}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-multiselectable={ariaMultiSelectable}
        aria-rowcount={ariaRowcount}
        aria-colcount={ariaColcount}>
        {data?.length || hasMore ? collectionComponent : <Fragment />}
      </div>
    );
  }
);

/**
 * Props specific for the BaseSwatchView
 */
type BaseSwatchViewProps<K, D> = BaseSwatchViewImplProps<K, D> & {
  ref?: ForwardedRef<HTMLDivElement>;
};

/**
 * Cast BaseSwatchViewImpl to generic type, because BaseSwatchViewImpl would infer the generic type to specific type,
 * i.e infer <K extends string | number, D> to <string | number, unknown>, which will cause type issues in the parent component
 */
export const BaseSwatchView = BaseSwatchViewImpl as <K extends string | number, D>(
  props: BaseSwatchViewProps<K, D>
) => JSX.Element;
